import React, { useState } from "react";
import {
    TableCell,
    TableRow,
    Typography,
    Stack,
    Switch,
    Button,
    Avatar,
    Box,
    Chip,
} from "@mui/material";

import TableCommon from "../../Common/Table";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { useUpdateProductsMutation } from "../../../store/masters/mastersApis";
import ActionMenu, { ActionTypes } from "../../Common/ActionMenu";
import ConfirmationDialog from "../../Common/ConfirmationDialog";
import { baseUrl } from "../../../helpers/constants";

const ProductsTableComponent = ({ loading, fetching = false, count, data, edit, pagination, handlePagination }) => {
    const [handleUpdate] = useUpdateProductsMutation();
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [productIdToDelete, setProductIdToDelete] = useState(null);

    const handleDisable = (row) => {
        setProductIdToDelete(row);
        setConfirmationOpen(true);
    };

    const handleConfirmDisable = () => {
        handleUpdate({
            id: productIdToDelete?.id,
            name: productIdToDelete?.name,
            image_url: productIdToDelete?.image_url,
            price: productIdToDelete?.price,
            description: productIdToDelete?.description,
            is_active: !productIdToDelete?.is_active
        });
        setConfirmationOpen(false);
    };

    const handleCloseConfirmation = () => {
        setConfirmationOpen(false);
    };

    let columns = [
        {
            title: "ID",
            field: "id",
            sort: true,
            width: "8%",
            align: "center",
        },
        {
            title: "Image",
            field: "image_url",
            sort: false,
            width: "12%",
            align: "center",
        },
        {
            title: "Product Name",
            field: "name",
            sort: true,
            width: "25%",
            align: "left",
        },
        {
            title: "Price",
            field: "price",
            sort: true,
            width: "15%",
            align: "center",
        },
        {
            title: "Description",
            field: "description",
            sort: false,
            width: "20%",
            align: "left",
        },
        {
            title: "Status",
            field: "is_active",
            sort: false,
            width: "10%",
            align: "center",
        },
        {
            title: "Created Date",
            field: "created_at",
            sort: true,
            width: "10%",
            align: "center",
        },
        {
            title: "",
            name: "",
            sort: false,
            width: "10%",
            align: "center",
        },
    ];

    // Helper to safely format price
    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") {
            return "-";
        }
        const num = Number(price);
        if (isNaN(num)) {
            return "-";
        }
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const renderTableData = !loading && data && data.length > 0 ? (
        data.map(function (row, index) {
            return (
                <TableRow
                    key={index}
                    sx={{ 
                        "td, th": { 
                            border: 0, 
                            padding: "16px 12px",
                            verticalAlign: "middle",
                        }, 
                        ...(row?.is_active === false && { 
                            opacity: 0.6,
                            backgroundColor: '#fafafa',
                        }),
                        '&:hover': {
                            backgroundColor: '#f8f9fa',
                        },
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    <TableCell align="center">
                        <Chip
                            label={row.id}
                            size="small"
                            sx={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                            }}
                        />
                    </TableCell>
                    <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            {row?.image_url ? (
                                <Avatar
                                    src={baseUrl + row.image_url}
                                    alt={row.name}
                                    sx={{ 
                                        width: 48, 
                                        height: 48,
                                        border: '2px solid #e0e0e0',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    }}
                                />
                            ) : (
                                <Avatar sx={{ 
                                    width: 48, 
                                    height: 48, 
                                    bgcolor: 'grey.200',
                                    border: '2px solid #e0e0e0',
                                }}>
                                    <Typography variant="caption" sx={{ color: 'grey.500', fontSize: '0.7rem' }}>
                                        No Image
                                    </Typography>
                                </Avatar>
                            )}
                        </Box>
                    </TableCell>
                    <TableCell align="left">
                        <Stack spacing={0.5}>
                            <Typography variant="body2" sx={{ 
                                fontWeight: 600,
                                color: '#2c3e50',
                                fontSize: '0.875rem',
                            }}>
                                {row?.name}
                            </Typography>
                            {row?.is_active === false && (
                                <Chip
                                    label="Inactive"
                                    size="small"
                                    sx={{
                                        backgroundColor: '#ffebee',
                                        color: '#d32f2f',
                                        fontSize: '0.7rem',
                                        height: 20,
                                        width: 'fit-content',
                                    }}
                                />
                            )}
                        </Stack>
                    </TableCell>
                    <TableCell align="center">
                        <Typography variant="body2" sx={{ 
                            fontWeight: 700,
                            color: '#2e7d32',
                            fontSize: '0.875rem',
                            backgroundColor: '#e8f5e8',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            display: 'inline-block',
                        }}>
                            ₹{formatPrice(row?.price)}
                        </Typography>
                        {row?.mrp ? (
                            <Typography variant="caption" display="block" sx={{ color: "text.secondary", mt: 0.5 }}>
                                MRP <s>₹{formatPrice(row.mrp)}</s>
                            </Typography>
                        ) : null}
                        <Typography variant="caption" display="block" sx={{ color: "text.secondary" }}>
                            {row?.images?.length || (row?.image_url ? 1 : 0)} image(s)
                        </Typography>
                    </TableCell>
                    <TableCell align="left">
                        <Typography variant="body2" sx={{ 
                            color: "text.secondary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            fontSize: '0.8rem',
                            lineHeight: 1.4,
                            maxWidth: '200px',
                        }}>
                            {row?.description || "No description available"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Chip
                            label={row?.is_active ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                                backgroundColor: row?.is_active ? '#e8f5e8' : '#ffebee',
                                color: row?.is_active ? '#2e7d32' : '#d32f2f',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                            }}
                        />
                    </TableCell>
                    <TableCell align="center">
                        <Typography variant="caption" sx={{ 
                            color: "text.secondary",
                            fontSize: '0.75rem',
                            fontWeight: 500,
                        }}>
                            {handleDateTimeDefault(row?.created_at)}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <ActionMenu
                            actions={[
                                {
                                    ...ActionTypes.VIEW,
                                    onClick: () => edit(row, "View")
                                },
                                {
                                    ...ActionTypes.EDIT,
                                    onClick: () => edit(row, "Edit")
                                }
                            ]}
                            row={row}
                            onStatusToggle={handleDisable}
                        />
                    </TableCell>
                </TableRow>
            );
        })
    ) : null;

    return (
        <>
            <TableCommon
                columns={columns}
                tableData={renderTableData}
                loading={loading}
                fetching={fetching}
                count={count}
                pagination={pagination}
                handlePagination={handlePagination}
            />
            <ConfirmationDialog
                open={confirmationOpen}
                title={`Are you sure you want to ${productIdToDelete?.is_active ? 'deactivate' : 'activate'} this product?`}
                confirmButtonTitle={productIdToDelete?.is_active === false ? "Yes, Activate" : "Yes, Deactivate"}
                onConfirm={handleConfirmDisable}
                onClose={handleCloseConfirmation}
            />
        </>
    );
};

export default ProductsTableComponent; 